

/**
=========================================================
* Material Dashboard 2 React - v2.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 React components
import { useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// v2 tone pairs for the change line (text on a tint, WCAG AA).
const V2_TONES = {
  success: ["#E5F4EB", "#166534"],
  error: ["#FDEBEA", "#B42318"],
  warning: ["#FDF0DC", "#8A4B08"],
  info: ["#E6EFFB", "#1453A0"],
  primary: ["#E6EFFB", "#1453A0"],
};

function V2StatisticsCard({ title, count, percentage }) {
  const [bg, fg] = V2_TONES[percentage?.color] || ["#EDF0F5", "#44546F"];
  return (
    <Card sx={{ p: 2.25, height: "100%" }}>
      <MDTypography component="div" sx={{ fontSize: 13, fontWeight: 500, lineHeight: 1.4, color: "#44546F" }}>
        {title}
      </MDTypography>
      <MDTypography
        sx={{
          // Currency totals ("A$ 2,617,581.18") stay on one line in a quarter-width card.
          fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)",
          fontWeight: 600,
          lineHeight: 1.2,
          letterSpacing: "-0.01em",
          mt: 1,
          whiteSpace: "nowrap",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {count}
      </MDTypography>
      {(percentage?.amount || percentage?.label) && (
        <MDBox
          component="span"
          sx={{ display: "inline-block", alignSelf: "flex-start", mt: 1.25, px: 1, py: 0.25, borderRadius: "10px", bgcolor: bg, color: fg, fontSize: "0.75rem", fontWeight: 600 }}
        >
          {percentage.amount} {percentage.label}
        </MDBox>
      )}
    </Card>
  );
}
V2StatisticsCard.propTypes = {
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  percentage: PropTypes.shape({ color: PropTypes.string, amount: PropTypes.node, label: PropTypes.string }),
};
V2StatisticsCard.defaultProps = { percentage: null };

function ComplexStatisticsCard({ color, title, count, percentage, icon }) {
  // v2: the prototype's KPI card (label, figure, change chip), no icon tile.
  if (useTheme()?.pearlUi === "v2") {
    return <V2StatisticsCard title={title} count={count} percentage={percentage} />;
  }
  return (
    <Card>
      <MDBox display="flex" justifyContent="space-between" pt={1} px={2}>
        <MDBox
          variant="gradient"
          bgColor={color}
          color={color === "light" ? "dark" : "white"}
          coloredShadow={color}
          borderRadius="xl"
          display="flex"
          justifyContent="center"
          alignItems="center"
          width="4rem"
          height="4rem"
          mt={-3}
        >
          <Icon fontSize="medium" color="inherit">
            {icon}
          </Icon>
        </MDBox>
        <MDBox textAlign="right" lineHeight={1.25}>
          <MDTypography variant="button" fontWeight="light" color="text">
            {title}
          </MDTypography>
          <MDTypography variant="h4">{count}</MDTypography>
        </MDBox>
      </MDBox>
      <Divider />
      <MDBox pb={2} px={2}>
        <MDTypography component="p" variant="button" color="text" display="flex">
          <MDTypography
            component="span"
            variant="button"
            fontWeight="bold"
            color={percentage.color}
          >
            {percentage.amount}
          </MDTypography>
          &nbsp;{percentage.label}
        </MDTypography>
      </MDBox>
    </Card>
  );
}

// Setting default values for the props of ComplexStatisticsCard
ComplexStatisticsCard.defaultProps = {
  color: "info",
  percentage: {
    color: "success",
    text: "",
    label: "",
  },
};

// Typechecking props for the ComplexStatisticsCard
ComplexStatisticsCard.propTypes = {
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  title: PropTypes.string.isRequired,
  count: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  percentage: PropTypes.shape({
    color: PropTypes.oneOf([
      "primary",
      "secondary",
      "info",
      "success",
      "warning",
      "error",
      "dark",
      "white",
    ]),
    amount: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
  }),
  icon: PropTypes.node.isRequired,
};

export default ComplexStatisticsCard;
